import type { DialogStore } from '@ariakit/react'
import { type FC, useState } from 'react'
import { Chip } from '@/components/commons/chip'
import { ErrorBox } from '@/components/commons/error-box'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import {
	DialogDismiss,
	DialogFooter,
	DialogHeader,
	DialogHeading,
	DialogProvider
} from '@/components/ui/dialog-atoms'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import {
	OpsVettingCheckIconContainer,
	OpsVettingChoosePhotoDialog,
	OpsVettingChoosePhotoDialogBody
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n'
import type { HttpGetExperienceVettingGalleryImagesResponse } from '../http/experience-vetting.http'
import { useGetExperienceVettingGalleryImages } from '../queries/use-get-experience-vetting-gallery-images'
import { useUpdateExperienceVettingCriteriaItineraryCoveryImage } from '../queries/use-update-experience-vetting-criteria-itinerary-covery-image'

const GalleryContent = ({
	isPending,
	isError,
	images,
	selectedImageId,
	onSelectImage
}: {
	isPending: boolean
	isError: boolean
	images: HttpGetExperienceVettingGalleryImagesResponse | undefined
	selectedImageId: number | undefined
	onSelectImage: (id: number) => void
}) => {
	const { t } = useTranslation()
	if (isPending) {
		return Array.from({ length: 6 }, (_, i) => `skeleton-${i}`).map(
			(key) => (
				<Skeleton
					className='aspect-square h-full w-full rounded-lg object-cover'
					key={key}
				/>
			)
		)
	}
	if (isError) {
		return (
			<ErrorBox
				icon={<AlertTriangleIcon />}
				title={t('errors:default')}
			/>
		)
	}
	return images?.map((image) => (
		<GalleryImageItem
			bottomLeftSlot={
				image.isProPhotography ? (
					<Chip
						label={t('vetting:reordering.pro_photo')}
						shape='roundedRectangle'
						variant={'neutral'}
					/>
				) : (
					image.isHostApprovalNeeded === false && (
						<Chip
							label={t('vetting:reordering.host_provided')}
							shape='roundedRectangle'
							variant={'neutral'}
						/>
					)
				)
			}
			className='h-full'
			isSelected={selectedImageId === image.id}
			key={image.id}
			onClick={() => onSelectImage(image.id)}
			src={image.url}
			topLeftSlot={
				selectedImageId === image.id && (
					<OpsVettingCheckIconContainer>
						<CheckIcon />
					</OpsVettingCheckIconContainer>
				)
			}
		/>
	))
}

interface ExpProPhotosChangeItineraryPhotoModalProps {
	itineraryId: number
	itineraryTitle: string
	itineraryCoverImageId?: number
	experienceId: number
	galleryId: number
	store: DialogStore
	onClose: () => void
}

export const ExpProPhotosChangeItineraryPhotoModal: FC<
	ExpProPhotosChangeItineraryPhotoModalProps
> = ({
	itineraryId,
	itineraryTitle,
	itineraryCoverImageId,
	experienceId,
	galleryId,
	store,
	onClose
}) => {
	const [selectedImageId, setSelectedImageId] = useState<number | undefined>(
		itineraryCoverImageId
	)
	const {
		data: images,
		isPending,
		isError
	} = useGetExperienceVettingGalleryImages({ experienceId, galleryId })
	const { t } = useTranslation()
	const updatePhotoMutation =
		useUpdateExperienceVettingCriteriaItineraryCoveryImage(
			{
				experienceId,
				itineraryId
			},
			{
				onSuccess: onClose
			}
		)

	const onConfirm = (id: number) => {
		updatePhotoMutation.mutate({ coverImageId: id })
	}

	return (
		<DialogProvider
			setOpen={() => {
				setSelectedImageId(itineraryCoverImageId)
			}}
			store={store}
		>
			<OpsVettingChoosePhotoDialog width='680px'>
				<DialogHeader className='relative'>
					<DialogDismiss />
					<div className='absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center'>
						<DialogHeading>
							{t('common:choose_a_photo')}
						</DialogHeading>
						<span className='text-xs'>{itineraryTitle}</span>
					</div>
				</DialogHeader>
				<ScrollArea scrollbar={<Scrollbar />}>
					<OpsVettingChoosePhotoDialogBody>
						<GalleryContent
							images={images}
							isError={isError}
							isPending={isPending}
							onSelectImage={setSelectedImageId}
							selectedImageId={selectedImageId}
						/>
					</OpsVettingChoosePhotoDialogBody>
				</ScrollArea>
				<DialogFooter>
					<DialogDismiss
						onClick={onClose}
						render={
							<ButtonLegacy type='button' variant='tertiary' />
						}
					>
						{t('common:cancel')}
					</DialogDismiss>

					<ButtonLegacy
						disabled={
							!selectedImageId || updatePhotoMutation.isPending
						}
						onClick={() =>
							selectedImageId && onConfirm(selectedImageId)
						}
						type='button'
					>
						{t('common:confirm')}
						{updatePhotoMutation.isPending && <Spinner />}
					</ButtonLegacy>
				</DialogFooter>
			</OpsVettingChoosePhotoDialog>
		</DialogProvider>
	)
}
